import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { apiService } from '../services/api';
import { CurrentUser } from '../types';

/** Who is signed in, and what they are allowed to do.
 *
 * The session itself lives in an httpOnly cookie the browser attaches
 * automatically -- nothing here holds a token, and nothing here can. This
 * context only mirrors what the server said, so hiding a button is a
 * convenience: every rule is enforced again on the server, which is the copy
 * that matters.
 */
interface AuthState {
  user: CurrentUser | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  /** May change things. */
  canEdit: boolean;
  /** Sees every brand, and so may manage users. */
  isSuperAdmin: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setUser(await apiService.getCurrentUser());
    } catch {
      // A 401 here is the normal "not signed in yet" case, not a failure.
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (username: string, password: string) => {
    setUser(await apiService.login(username, password));
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiService.logout();
    } finally {
      setUser(null);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        refresh,
        canEdit: user?.role === 'admin',
        isSuperAdmin: user?.role === 'admin' && user?.brand_id === null,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside an AuthProvider');
  return ctx;
}
